import { Button, LoadingModal } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import Form from "components/Forms/Form";
import FormButtons from "components/Forms/FormButtons";
import { ModalFormFieldset } from "components/Forms/FormRenderers/ModalFormRenderer";
import AppModal from "components/common/AppModal";
import { SectionTabs } from "components/common/SectionTabs";
import { funderTypeLabel } from "lib/fundingStatusLabels";
import { injectModal } from "lib/injectParams";
import { useEffect, useMemo, useState } from "react";
import { FundingOutreachFormController } from "components/Pages/Funding/FundingOutreachFormController";

const ADD_FUNDER_MODAL_TABS = [
  { id: "select", label: "Select funders" },
  { id: "add", label: "Add funder" },
] as const;

const NewApplicationModal: React.FC<{ clubId: string; appealId: string }> = () => {
  const controller = FundingOutreachFormController.use();
  const { saving, loading, funders, funderIdsAlreadyOnAppeal, bulkSelectedFunderIds, errors } =
    controller.state;
  const [activeTabId, setActiveTabId] = useState<(typeof ADD_FUNDER_MODAL_TABS)[number]["id"]>("select");

  useEffect(() => {
    if (activeTabId === "add") {
      controller.actionPrepareNewFunderTab();
    }
  }, [activeTabId, controller]);

  const sortedFunders = useMemo(
    () =>
      [...funders].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }),
      ),
    [funders],
  );

  const newFunderTabFields = useMemo(() => {
    const f = controller.fields();
    const { funder_id: _drop, ...rest } = f;
    return Object.values(rest);
  }, [controller, funders, controller.state.record?.funder_id]);

  const selectableFunders = useMemo(
    () => sortedFunders.filter((f) => f.id && !funderIdsAlreadyOnAppeal.includes(f.id)),
    [sortedFunders, funderIdsAlreadyOnAppeal],
  );

  if (loading) return <LoadingModal title="Add funder" />;

  return (
    <AppModal title="Add funder" className="max-h-[80vh] sm:max-w-lg">
      <div className="flex flex-col gap-4">
        <div className="-mx-6">
          <SectionTabs
            className="px-6"
            tabs={[...ADD_FUNDER_MODAL_TABS]}
            activeTabId={activeTabId}
            onChange={(id) => setActiveTabId(id as (typeof ADD_FUNDER_MODAL_TABS)[number]["id"])}
          />
        </div>

        {activeTabId === "select" && (
          <>
            {errors._bulk && (
              <p className="text-sm text-red-600" role="alert">
                {errors._bulk}
              </p>
            )}
            <div className="max-h-[min(50vh,22rem)] overflow-y-auto rounded-md border border-gray-200">
              {selectableFunders.length === 0 ? (
                <p className="p-4 text-sm text-gray-600">
                  {sortedFunders.length === 0
                    ? "You have no funders yet. Use the Add funder tab to create one."
                    : "Every funder is already on this appeal. Use the Add funder tab to create a new one."}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {selectableFunders.map((f) => {
                    const id = f.id!;
                    const checked = bulkSelectedFunderIds.includes(id);
                    const typeLabel = f.funder_type ? funderTypeLabel(f.funder_type) : undefined;
                    return (
                      <li key={id}>
                        <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={checked}
                            disabled={saving}
                            onChange={() => controller.actionToggleBulkFunderSelection(id)}
                          />
                          <span className="min-w-0 flex-1 text-sm">
                            <span className="font-medium text-gray-900">{f.name ?? "—"}</span>
                            {typeLabel ? (
                              <span className="mt-0.5 block text-xs text-gray-500">{typeLabel}</span>
                            ) : null}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {funderIdsAlreadyOnAppeal.length > 0 && selectableFunders.length > 0 && (
              <p className="text-xs text-gray-500">
                Funders already linked to this appeal are hidden from the list.
              </p>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4">
              <Button type="button" kind="plain" disabled={saving} onClick={() => controller.actionBack()}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={saving || bulkSelectedFunderIds.length === 0}
                spinner={saving}
                onClick={() => void controller.actionAddSelectedFunders()}
              >
                Add selected{bulkSelectedFunderIds.length > 0 ? ` (${bulkSelectedFunderIds.length})` : ""}
              </Button>
            </div>
          </>
        )}

        {activeTabId === "add" && (
          <Form name="funding_outreach">
            <fieldset disabled={saving}>
              <ModalFormFieldset fields={newFunderTabFields} />
            </fieldset>
            <div className="mt-4 flex justify-end">
              <FormButtons showDelete={false} showCancel saveTitle="Add" />
            </div>
          </Form>
        )}
      </div>
    </AppModal>
  );
};

export default injectModal(FundingOutreachFormController.scope(ApplicationView(NewApplicationModal)));
